import API from "../../services/api";

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match?.[1] || fallback;
}

export async function parseTdsApiError(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      if (typeof json.detail === "string") return json.detail;
      if (Array.isArray(json.detail)) {
        return json.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      }
      return text || "Request failed";
    } catch {
      return "Form 16 download failed";
    }
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return err?.message || "Request failed";
}

export async function fetchForm16FinancialYears() {
  const { data } = await API.get("/tds/form16/financial-years");
  return data;
}

/** Map API employee tax row to UI shape (camelCase). */
export function mapTaxEmployeeRow(row) {
  if (!row) return null;
  return {
    id: row.employee_id,
    code: row.code,
    name: row.name,
    regime: row.regime,
    declarationStatus: row.declaration_status,
    proofStatus: row.proof_status,
    monthlyTds: row.monthly_tds,
    grossIncome: row.gross_income,
    exemptions: row.exemptions || {
      section80C: 0,
      section80D: 0,
      hra: 0,
      homeLoan: 0,
    },
    taxableIncome: row.taxable_income,
    annualTax: row.annual_tax,
    department: row.department,
    designation: row.designation,
  };
}

export async function fetchEmployeeTaxRegister() {
  const { data } = await API.get("/tds/employees");
  return {
    employees: (data.employees || []).map(mapTaxEmployeeRow),
    stats: {
      total: data.stats?.total ?? 0,
      taxable: data.stats?.taxable ?? 0,
      declarationsPending: data.stats?.declarations_pending ?? 0,
      proofsPending: data.stats?.proofs_pending ?? 0,
    },
  };
}

export async function updateEmployeeDeclaration(employeeId, declarationStatus) {
  const { data } = await API.patch(`/tds/employees/${employeeId}/declaration`, {
    declaration_status: declarationStatus,
  });
  return mapTaxEmployeeRow(data);
}

/**
 * Download Form 16 PDF (single employee) or ZIP (all employees).
 * @param {{ employeeId?: string, financialYear?: string, signatory?: object }} opts
 */
export async function downloadForm16({
  employeeId = null,
  financialYear = null,
  signatory = {},
} = {}) {
  const res = await API.post(
    "/tds/form16/download",
    {
      employee_id: employeeId || null,
      financial_year: financialYear || null,
      signatory: {
        signatory_name: signatory.signatoryName || signatory.signatory_name || "",
        designation: signatory.designation || "",
        place: signatory.place || "",
        digital_signature: signatory.digitalSignature || signatory.digital_signature || null,
      },
    },
    { responseType: "blob" }
  );

  const fallback = employeeId
    ? `Form16_${employeeId}.pdf`
    : "Form16_AllEmployees.zip";
  const filename = filenameFromDisposition(
    res.headers?.["content-disposition"],
    fallback
  );
  triggerBlobDownload(new Blob([res.data]), filename);
  return filename;
}
